import { t } from "@lingui/macro";
import { CALL_STATUS } from "@repo/common";
import {
	createActionItem,
	db,
	hashTranscriptText,
	hasOutboundCallMetadata,
	type OutboundCallContext,
	payloadEntries,
	resolveCallParticipantDisplay,
	saveTranscriptEdit,
	TranscriptEditConflictError,
	updateActionItem,
	updateCall,
	updateCaller,
} from "@repo/ui";
import * as Sentry from "@sentry/react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import {
	AlertCircle,
	ArrowLeft,
	ArrowRight,
	Calendar,
	CheckCircle,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Clock,
	Download,
	FileText,
	FlaskConical,
	Loader2,
	Mail,
	MessageCircle,
	Pencil,
	Phone,
	PhoneForwarded,
	Play,
	Plus,
	StickyNote,
	Tag,
	User,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { useCallers } from "../hooks/useCallers";
import { useMarkCallReadOnOpen } from "../hooks/useMarkCallReadOnOpen";
import { useServiceTags } from "../hooks/useServiceTags";
import { i18n } from "../i18n";
import { type ActionItem, type Call } from "../types";
import { callerDocumentId, customerPhone } from "../utils/callerDirectory";
import { resolveCallServiceId } from "../utils/callServices";
import { formatCaseId } from "../utils/formatCaseId";
import { formatPhoneNumber, isPhoneKey } from "../utils/formatPhoneNumber";
import {
	getServiceTagClassName,
	getVisibleServiceTags,
	hasServiceTagReference,
	normalizeTagIds,
	resolveTagIds,
} from "../utils/serviceTags";
import { getContentFieldProps } from "../utils/textDirection";
import { formatTranscript } from "../utils/transcript";
import { CallArchiveButton } from "./CallArchiveButton";
import { CallerInstructionsCard } from "./CallerInstructionsCard";
import { CallerMemoryCard } from "./CallerMemoryCard";
import { CallNavigationControls } from "./CallNavigationControls";
import { TranscriptFeedback } from "./TranscriptFeedback";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

interface SmsLog {
	to: string;
	body: string;
	from: string;
	twilioSid: string;
	timestamp: { toDate?: () => Date } | null;
}

interface CallDetailsProps {
	call: Call;
	onBack: () => void;
	onNavigateToCall?: (callId: string) => void;
	previousCallId?: string | null;
	nextCallId?: string | null;
	shortcutsEnabled?: boolean;
	onPlayAudio?: () => void;
	outboundContext?: OutboundCallContext;
}

interface ActionItemState {
	id?: string;
	originalItem: ActionItem;
	title: string;
	description: string;
	notes: string;
	status: "open" | "in-progress" | "closed";
	priority: "low" | "normal" | "urgent";
	tags: string[];
}

function getDefaultActionItemTitle(item: ActionItem): string {
	return item.action?.type === "followUp"
		? t({ id: "followUpActionItemTitle", message: "Follow Up" })
		: t({ id: "bookingActionItemTitle", message: "Booking" });
}

function mapActionItemToState(item: ActionItem): ActionItemState {
	const isFollowUp = item.action?.type === "followUp";
	const details = isFollowUp
		? (item.action as import("@repo/common").FollowUpActionDetails).details
		: (item.action as import("@repo/common").BookingActionDetails)
				.accommodationType;
	const urgency = isFollowUp
		? (item.action as import("@repo/common").FollowUpActionDetails).urgency
		: "normal";

	return {
		originalItem: item,
		id: item.id,
		title: item.title?.trim() || getDefaultActionItemTitle(item),
		description: details || "",
		notes: item.notes || "",
		status: item.status,
		priority: urgency || "normal",
		tags: item.tags || [],
	};
}

export function CallDetails({
	call: initialCall,
	onBack,
	onNavigateToCall,
	previousCallId,
	nextCallId,
	shortcutsEnabled = true,
	onPlayAudio,
	outboundContext,
}: CallDetailsProps) {
	const {
		language,
		calls,
		agents,
		services,
		user,
		actionItems: _actionItems,
	} = useData();
	const contentFieldProps = (
		value: string | null | undefined,
		extraClassName = "",
	) =>
		getContentFieldProps(
			value,
			language === "he" ? "rtl" : "ltr",
			extraClassName,
		);

	// Use live data from context if available, fallback to initial prop

	const baseCall = calls.find((c) => c.id === initialCall.id) || initialCall;
	const callerPhone = customerPhone(baseCall);
	const callerId = callerDocumentId(callerPhone);
	const { callers: contactRecords } = useCallers(
		user?.companyId,
		callerId || "_unknown",
	);
	const contact = contactRecords[0];
	const call = useMemo(
		() =>
			contact
				? {
						...baseCall,
						caller: {
							...baseCall.caller,
							name: contact.name || baseCall.caller.name,
							email: contact.email || baseCall.caller.email,
							tags: contact.tags,
							notes: contact.notes || undefined,
						},
					}
				: baseCall,
		[baseCall, contact],
	);

	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [editFormData, setEditFormData] = useState({
		name: call.caller.name,
		email: call.caller.email || "",
		notes: call.caller.notes || "",
		tags: call.caller.tags || [],
	});
	const [newTag, setNewTag] = useState("");

	// Call info editing state
	const [isEditCallInfoOpen, setIsEditCallInfoOpen] = useState(false);
	const [callInfoFormData, setCallInfoFormData] = useState({
		notes: call.notes || "",
		tagIds: call.tagIds || [],
	});
	const [callInfoTagIdToAdd, setCallInfoTagIdToAdd] = useState<
		string | undefined
	>(undefined);

	// Action item editing state
	const [isEditActionItemOpen, setIsEditActionItemOpen] = useState(false);
	const [editingActionItemIndex, setEditingActionItemIndex] = useState<
		number | null
	>(null);
	const [actionItemFormData, setActionItemFormData] = useState({
		title: "",
		description: "",
		notes: "",
		status: "open" as "open" | "in-progress" | "closed",
		priority: "normal" as "low" | "normal" | "urgent",
		tags: [] as string[],
	});
	const [actionItemNewTag, setActionItemNewTag] = useState("");

	// Create action item state
	const [isCreateActionItemOpen, setIsCreateActionItemOpen] = useState(false);
	const [createActionItemFormData, setCreateActionItemFormData] = useState({
		title: "",
		description: "",
		notes: "",
		status: "open" as "open" | "in-progress" | "closed",
		priority: "normal" as "low" | "normal" | "urgent",
		tags: [] as string[],
	});
	const [createActionItemNewTag, setCreateActionItemNewTag] = useState("");

	// Sent SMS messages state
	const [smsLogs, setSmsLogs] = useState<SmsLog[]>([]);
	const [transcriptFeedback, setTranscriptFeedback] = useState(
		call.transcriptFeedback,
	);
	const [transcriptText, setTranscriptText] = useState(call.transcript ?? "");
	const [isTranscriptEditOpen, setIsTranscriptEditOpen] = useState(false);
	const [transcriptEditText, setTranscriptEditText] = useState("");
	const [transcriptEditBaseHash, setTranscriptEditBaseHash] = useState("");
	const [isSavingTranscriptEdit, setIsSavingTranscriptEdit] = useState(false);

	useEffect(() => {
		if (!user?.companyId || !call.id) return;
		const q = query(
			collection(db, "companies", user.companyId!, "calls", call.id, "smsLogs"),
			orderBy("timestamp", "asc"),
		);
		const unsub = onSnapshot(
			q,
			(snapshot) => {
				setSmsLogs(snapshot.docs.map((doc) => doc.data() as SmsLog));
			},
			(err) => {
				console.error("Error fetching smsLogs", err);
			},
		);
		return unsub;
	}, [user?.companyId, call.id]);

	// Initialize action items with status and severity
	const [actionItems, setActionItems] = useState<ActionItemState[]>(() => {
		return (call.actionItems || []).map((item) => mapActionItemToState(item));
	});

	// Sync local state when call data updates (e.g. from external persistence)
	useEffect(() => {
		setTranscriptFeedback(call.transcriptFeedback);
		setTranscriptText(call.transcript ?? "");
		setEditFormData({
			name: call.caller.name,
			email: call.caller.email || "",
			notes: call.caller.notes || "",
			tags: call.caller.tags || [],
		});
		setCallInfoFormData({
			notes: call.notes || "",
			tagIds: call.tagIds || [],
		});

		// Update action items state mapping
		const mappedActionItems = (call.actionItems || []).map((item) =>
			mapActionItemToState(item),
		);
		setActionItems(mappedActionItems);
	}, [call]);

	const handleTranscriptEditOpen = async () => {
		const currentText = transcriptText;
		setTranscriptEditText(currentText);
		setTranscriptEditBaseHash(
			call.transcriptEdit?.hash ?? (await hashTranscriptText(currentText)),
		);
		setIsTranscriptEditOpen(true);
	};

	const handleTranscriptEditSave = async () => {
		if (
			!user?.companyId ||
			!user.uid ||
			!call.id ||
			!transcriptEditText.trim() ||
			transcriptEditText === transcriptText
		) {
			return;
		}

		setIsSavingTranscriptEdit(true);
		try {
			await saveTranscriptEdit(db, user.companyId!, call.id, {
				text: transcriptEditText,
				editedBy: user.uid,
				expectedHash: transcriptEditBaseHash,
			});
			setTranscriptText(transcriptEditText);
			setIsTranscriptEditOpen(false);
			toast.success(
				t({
					id: "transcriptEdit.saved",
					message: "Transcript saved",
				}),
			);
		} catch (error) {
			console.error("Error saving transcript edit:", error);
			Sentry.captureException(error);
			toast.error(
				error instanceof TranscriptEditConflictError
					? t({
							id: "transcriptEdit.conflict",
							message:
								"The transcript was edited by someone else. Reopen it and try again.",
						})
					: t({
							id: "transcriptEdit.saveFailed",
							message: "Failed to save transcript",
						}),
			);
		} finally {
			setIsSavingTranscriptEdit(false);
		}
	};

	const handleTranscriptFeedback = async (
		rating: "positive" | "negative",
		comment?: string,
	) => {
		if (!user?.companyId || !user.uid || !call.id) return;

		const previousFeedback = transcriptFeedback;
		const nextFeedback = {
			rating,
			comment: rating === "negative" ? comment?.trim() || null : null,
			submittedBy: user.uid,
			submittedAt: new Date(),
		};
		setTranscriptFeedback(nextFeedback);

		try {
			await updateCall(db, user.companyId!, call.id, {
				transcriptFeedback: nextFeedback,
			});
			if (comment?.trim()) {
				toast.success(
					t({
						id: "transcriptFeedback.saved",
						message: "Thanks for your feedback",
					}),
				);
			}
		} catch (error) {
			setTranscriptFeedback(previousFeedback);
			console.error("Error saving transcript feedback:", error);
			Sentry.captureException(error);
			toast.error(
				t({
					id: "transcriptFeedback.saveFailed",
					message: "Failed to save transcript feedback",
				}),
			);
			throw error;
		}
	};

	const formattedTranscript = useMemo(() => {
		return formatTranscript(transcriptText, language);
	}, [transcriptText, language]);
	const collectedNumbers = call.collectedNumbers ?? [];
	const hasNoTranscript = !transcriptText.trim();
	const isProcessingCall =
		hasNoTranscript &&
		(call.status === CALL_STATUS.PROCESSING ||
			call.status === CALL_STATUS.IN_PROGRESS);
	const isFailedWithoutTranscript =
		hasNoTranscript && call.status === CALL_STATUS.FAILED;
	const shouldShowNoAudio =
		hasNoTranscript &&
		(call.status === CALL_STATUS.COMPLETED ||
			call.status === CALL_STATUS.ABANDONED);
	const shouldShowStatusFallback =
		isProcessingCall || isFailedWithoutTranscript || shouldShowNoAudio;
	const fallbackSummary = isProcessingCall
		? t({
				id: "call_processing_summary",
				message:
					"Call processing is still in progress. Transcript and summary will appear when processing finishes.",
			})
		: isFailedWithoutTranscript
			? t({
					id: "call_processing_failed_summary",
					message: "Call processing failed before a transcript was created.",
				})
			: t({
					id: "no_audio_summary",
					message: "The call contained no recognizable audio or speech.",
				});
	const transcriptFallback = isProcessingCall
		? t({
				id: "call_processing_transcript",
				message: "Transcript is still being processed.",
			})
		: isFailedWithoutTranscript
			? t({
					id: "call_processing_failed_transcript",
					message: "Transcript processing failed.",
				})
			: t({
					id: "noTranscript",
					message: "No transcript available",
				});

	// Section refs for scroll tracking
	const summaryRef = useRef<HTMLDivElement>(null);
	const actionItemsRef = useRef<HTMLDivElement>(null);
	const transcriptRef = useRef<HTMLDivElement>(null);
	const callerDetailsRef = useRef<HTMLDivElement>(null);
	const tabBarRef = useRef<HTMLDivElement>(null);

	// Mobile action bar state
	const [activeSection, setActiveSection] = useState("summary");
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const inputSchema = useMemo(
		() =>
			outboundContext?.playbook?.inputSchema ||
			outboundContext?.run?.playbookSnapshot?.inputSchema,
		[outboundContext?.playbook, outboundContext?.run],
	);
	const resultSchema = useMemo(
		() =>
			outboundContext?.playbook?.resultSchema ||
			outboundContext?.run?.playbookSnapshot?.resultSchema,
		[outboundContext?.playbook, outboundContext?.run],
	);

	const outboundInputEntries = useMemo(
		() => payloadEntries(outboundContext?.taskInput, inputSchema),
		[outboundContext?.taskInput, inputSchema],
	);
	const outboundResultEntries = useMemo(
		() => payloadEntries(outboundContext?.structuredResult, resultSchema),
		[outboundContext?.structuredResult, resultSchema],
	);
	const isOutbound = useMemo(
		() => Boolean(outboundContext?.isOutbound || hasOutboundCallMetadata(call)),
		[outboundContext, call],
	);
	const participantDisplay = useMemo(
		() => resolveCallParticipantDisplay(call, outboundContext),
		[call, outboundContext],
	);
	const contactDisplayName = useMemo(() => {
		if (isOutbound) {
			return participantDisplay.callee.name !== "Unknown"
				? participantDisplay.callee.name
				: call.caller.name;
		}
		return call.caller.name;
	}, [isOutbound, participantDisplay.callee.name, call.caller.name]);
	const contactDisplayPhone = useMemo(() => {
		const rawPhone = isOutbound
			? participantDisplay.callee.phone || call.caller.phone
			: call.caller.phone;
		return formatPhoneNumber(rawPhone);
	}, [isOutbound, participantDisplay.callee.phone, call.caller.phone]);

	// Get all calls from this caller or callee
	const callerHistory = useMemo(() => {
		const targetPhone = contactDisplayPhone || call.caller.phone;
		if (!targetPhone) return [];
		const history = calls.filter(
			(c) => c.caller.phone === targetPhone || c.inboundNumber === targetPhone,
		);
		return history.sort((a, b) => {
			const dateA = new Date(a.date);
			const dateB = new Date(b.date);
			return dateB.getTime() - dateA.getTime(); // Most recent first
		});
	}, [calls, contactDisplayPhone, call.caller.phone]);

	useMarkCallReadOnOpen(user?.companyId, call.id, call.interactionStatus);

	const handleToggleArchive = async () => {
		if (!user?.companyId || !call.id) return;
		const newStatus =
			call.interactionStatus === "archived" ? "read" : "archived";
		try {
			await updateCall(db, user.companyId!, call.id, {
				interactionStatus: newStatus,
			});
			toast.success(
				newStatus === "archived"
					? t({ id: "callArchived", message: "Call archived" })
					: t({ id: "callUnarchived", message: "Call unarchived" }),
			);
			if (newStatus === "archived") {
				onBack(); // Go back to list after archiving
			}
		} catch (error) {
			console.error("Error toggling archive status:", error);
			Sentry.captureException(error);
			toast.error("Failed to update archive status");
		}
	};

	const handleMarkUnread = async () => {
		if (!user?.companyId || !call.id) return;
		try {
			await updateCall(db, user.companyId!, call.id, {
				interactionStatus: "unread",
			});
			toast.success(
				t({ id: "callMarkedUnread", message: "Call marked as unread" }),
			);
			onBack();
		} catch (error) {
			console.error("Error marking call as unread:", error);
			Sentry.captureException(error);
			toast.error("Failed to mark call as unread");
		}
	};

	const lastCall = callerHistory[0];
	const firstCall = callerHistory[callerHistory.length - 1];
	const resolvedServiceId = useMemo(
		() => resolveCallServiceId(call, agents, services),
		[call, agents, services],
	);
	const serviceTags = useServiceTags(user?.companyId, resolvedServiceId);
	const activeServiceTags = getVisibleServiceTags(serviceTags);
	const callTags = resolveTagIds(
		call.tagIds || callInfoFormData.tagIds,
		serviceTags,
	);
	const selectedCallInfoTags = resolveTagIds(
		callInfoFormData.tagIds,
		serviceTags,
	);
	const availableCallInfoTags = activeServiceTags.filter(
		(tag) => !hasServiceTagReference(callInfoFormData.tagIds, tag),
	);
	const callNotes = call.notes || callInfoFormData.notes;
	const formattedCallTime = useMemo(() => {
		const match = call.time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
		if (!match) return call.time;

		let hours = Number.parseInt(match[1], 10);
		const minutes = match[2];
		const meridiem = match[3].toUpperCase();

		if (meridiem === "AM") {
			hours = hours === 12 ? 0 : hours;
		} else if (hours !== 12) {
			hours += 12;
		}

		return `${String(hours).padStart(2, "0")}:${minutes}`;
	}, [call.time]);

	// Handle edit dialog open
	const handleEditOpen = () => {
		setEditFormData({
			name: contactDisplayName,
			email: call.caller.email || "",
			notes: call.caller.notes || "",
			tags: call.caller.tags || [],
		});
		setNewTag("");
		setIsEditDialogOpen(true);
	};

	// Handle save changes
	const handleSave = async () => {
		if (!user?.companyId || !call.caller.phone) return;

		try {
			await updateCaller(db, user.companyId!, callerId, {
				name: editFormData.name,
				email: editFormData.email || null,
				notes: editFormData.notes || null,
				tags: editFormData.tags,
			});

			// Optimistic update (local call object)
			call.caller.name = editFormData.name;
			call.caller.email = editFormData.email;
			call.caller.notes = editFormData.notes;
			call.caller.tags = editFormData.tags;
			setIsEditDialogOpen(false);
			toast.success(
				isOutbound
					? t({
							id: "calleeDetailsUpdated",
							message: "Callee details updated",
						})
					: t({
							id: "callerDetailsUpdated",
							message: "Caller details updated",
						}),
			);
		} catch (error) {
			console.error("Error updating caller/callee:", error);
			Sentry.captureException(error);
			toast.error(
				isOutbound
					? t({
							id: "failedToUpdateCalleeDetails",
							message: "Failed to update callee details",
						})
					: t({
							id: "failedToUpdateCallerDetails",
							message: "Failed to update caller details",
						}),
			);
		}
	};

	// Handle add tag
	const handleAddTag = () => {
		if (newTag.trim() && !editFormData.tags.includes(newTag.trim())) {
			setEditFormData({
				...editFormData,
				tags: [newTag.trim(), ...editFormData.tags],
			});
			setNewTag("");
		}
	};

	// Handle remove tag
	const handleRemoveTag = (tagToRemove: string) => {
		setEditFormData({
			...editFormData,
			tags: editFormData.tags.filter((tag) => tag !== tagToRemove),
		});
	};

	// Handle edit call info open
	const handleEditCallInfoOpen = () => {
		setCallInfoFormData({
			notes: call.notes || "",
			tagIds: normalizeTagIds(call.tagIds || [], serviceTags),
		});
		setCallInfoTagIdToAdd(undefined);
		setIsEditCallInfoOpen(true);
	};

	// Handle save call info changes
	const handleSaveCallInfo = async () => {
		if (!user?.companyId || !call.id) return;

		try {
			const normalizedTagIds = normalizeTagIds(
				callInfoFormData.tagIds,
				serviceTags,
			);
			await updateCall(db, user.companyId!, call.id, {
				notes: callInfoFormData.notes,
				tagIds: normalizedTagIds,
			});

			// Optimistic update
			call.notes = callInfoFormData.notes;
			call.tagIds = normalizedTagIds;
			setCallInfoFormData((current) => ({
				...current,
				tagIds: normalizedTagIds,
			}));
			setIsEditCallInfoOpen(false);
			toast.success("Call info updated");
		} catch (error) {
			console.error("Error updating call:", error);
			Sentry.captureException(error);
			toast.error("Failed to update call info");
		}
	};

	// Handle add tag to call info
	const handleSelectCallInfoTag = (tagId: string) => {
		if (!tagId) {
			setCallInfoTagIdToAdd(undefined);
			return;
		}

		setCallInfoFormData((current) => {
			const selectedTag = serviceTags.find((tag) => tag.id === tagId);
			if (selectedTag && hasServiceTagReference(current.tagIds, selectedTag)) {
				return current;
			}
			return {
				...current,
				tagIds: [tagId, ...current.tagIds],
			};
		});
		setCallInfoTagIdToAdd(undefined);
	};

	// Handle remove tag from call info
	const handleRemoveCallInfoTag = (tagToRemove: string) => {
		const tagToRemoveDefinition = serviceTags.find(
			(tag) => tag.id === tagToRemove,
		);
		setCallInfoFormData((current) => ({
			...current,
			tagIds: current.tagIds.filter((tag) => {
				if (tag === tagToRemove) {
					return false;
				}
				if (tagToRemoveDefinition && tag === tagToRemoveDefinition.label) {
					return false;
				}
				return true;
			}),
		}));
	};

	// Handle play recording
	const handlePlayRecording = () => {
		if (onPlayAudio) {
			onPlayAudio();
		} else if (call.gcsUri) {
			// Implement playback logic or alert as placeholder if needed
			alert("Playing recording: " + call.gcsUri);
		} else {
			alert("No recording available");
		}
	};

	// Handle edit action item open
	const handleEditActionItemOpen = (index: number) => {
		const item = actionItems[index];
		setActionItemFormData({
			title: item.title,
			description: item.description,
			notes: item.notes || "",
			status: item.status,
			priority: item.priority,
			tags: item.tags || [],
		});
		setActionItemNewTag("");
		setEditingActionItemIndex(index);
		setIsEditActionItemOpen(true);
	};

	// Handle save action item changes
	const handleSaveActionItem = async () => {
		if (editingActionItemIndex !== null && user?.companyId) {
			const itemToUpdate = actionItems[editingActionItemIndex];
			const savedTitle = actionItemFormData.title.trim();
			const defaultTitle = getDefaultActionItemTitle(itemToUpdate.originalItem);
			// Optimistic Update
			const updatedActionItems = actionItems.map((item, index) => {
				if (index === editingActionItemIndex) {
					return {
						...item,
						title: savedTitle || defaultTitle,
						description: actionItemFormData.description,
						notes: actionItemFormData.notes,
						status: actionItemFormData.status,
						priority: actionItemFormData.priority,
						tags: actionItemFormData.tags,
					};
				}
				return item;
			});
			setActionItems(updatedActionItems);
			setIsEditActionItemOpen(false);

			// Persist to DB
			if (itemToUpdate.id) {
				try {
					// Construct the update payload.
					// We need to preserve the existing 'action' object structure but update valid fields.
					// Since 'title' and 'description' in UI map to specific fields in 'action', we need to reverse map.

					const currentAction = itemToUpdate.originalItem.action;
					let updatedAction = { ...currentAction };

					if (updatedAction.type === "followUp") {
						// 'description' in UI maps to 'details'
						updatedAction = {
							...updatedAction,
							details: actionItemFormData.description,
						};
						// 'priority' in UI maps to 'urgency'
						updatedAction = {
							...updatedAction,
							urgency: actionItemFormData.priority as "normal" | "urgent",
						}; // Cast or validate
					} else if (updatedAction.type === "booking") {
						// 'description' in UI maps to 'accommodationType' (based on initial mapping)
						updatedAction = {
							...updatedAction,
							accommodationType: actionItemFormData.description,
						};
					}

					await updateActionItem(
						db,
						user.companyId!,
						itemToUpdate.id,
						{
							status: actionItemFormData.status,
							title: savedTitle,
							notes: actionItemFormData.notes,
							tags: actionItemFormData.tags,
							action: updatedAction,
						},
						user.uid,
					);
					toast.success("Action item updated successfully");
				} catch (error) {
					console.error("Error updating action item:", error);
					Sentry.captureException(error);
					setActionItems(actionItems);
					toast.error("Failed to update action item");
				}
			}
		}
	};

	const handleCreateActionItemOpen = () => {
		let defaultTitle = t({
			id: "followUpActionItemTitle",
			message: "Follow Up",
		});
		let defaultDesc = "";
		const callerName =
			contactDisplayName !== "Unknown" ? contactDisplayName : "";
		if (callerName) {
			const followUpWithText = t({
				id: "followUpWith",
				message: "Follow up with",
			});
			defaultTitle = `${followUpWithText} ${callerName}`;
			defaultDesc = `${followUpWithText} ${callerName}. Phone: ${formatPhoneNumber(contactDisplayPhone)}`;
		}

		setCreateActionItemFormData({
			title: defaultTitle,
			description: defaultDesc,
			notes: "",
			status: "open",
			priority: "normal",
			tags: [],
		});
		setCreateActionItemNewTag("");
		setIsCreateActionItemOpen(true);
	};

	const handleCreateActionItem = async () => {
		if (!user?.companyId || !call.id) return;

		try {
			const savedTitle = createActionItemFormData.title.trim();
			const actionDetails: import("@repo/common").FollowUpActionDetails = {
				type: "followUp",
				customerName:
					contactDisplayName !== "Unknown"
						? contactDisplayName
						: call.caller.name || "",
				phoneNumber: contactDisplayPhone || call.caller.phone || "",
				email: call.caller.email || "",
				urgency: createActionItemFormData.priority,
				details: createActionItemFormData.description,
				agent: call.agent || "",
			};

			const newId = await createActionItem(
				db,
				user.companyId!,
				{
					callSid: call.id,
					status: createActionItemFormData.status,
					type: "manual",
					title: savedTitle,
					action: actionDetails,
					notes: createActionItemFormData.notes,
					tags: createActionItemFormData.tags,
				},
				user.uid,
			);

			const newItemState: ActionItemState = {
				originalItem: {
					id: newId,
					callSid: call.id,
					createdAt: new Date(),
					status: createActionItemFormData.status,
					type: "manual",
					title: savedTitle,
					action: actionDetails,
					notes: createActionItemFormData.notes,
					tags: createActionItemFormData.tags,
				},
				id: newId,
				title:
					savedTitle ||
					t({ id: "followUpActionItemTitle", message: "Follow Up" }),
				description: createActionItemFormData.description,
				notes: createActionItemFormData.notes,
				status: createActionItemFormData.status,
				priority: createActionItemFormData.priority,
				tags: createActionItemFormData.tags,
			};

			setActionItems([newItemState, ...actionItems]);
			setIsCreateActionItemOpen(false);
			toast.success("Action item created successfully");
		} catch (error) {
			console.error("Error creating action item:", error);
			Sentry.captureException(error);
			toast.error("Failed to create action item");
		}
	};

	const handleAddCreateActionItemTag = () => {
		if (
			createActionItemNewTag.trim() &&
			!createActionItemFormData.tags.includes(createActionItemNewTag.trim())
		) {
			setCreateActionItemFormData({
				...createActionItemFormData,
				tags: [createActionItemNewTag.trim(), ...createActionItemFormData.tags],
			});
			setCreateActionItemNewTag("");
		}
	};

	const handleRemoveCreateActionItemTag = (tagToRemove: string) => {
		setCreateActionItemFormData({
			...createActionItemFormData,
			tags: createActionItemFormData.tags.filter((tag) => tag !== tagToRemove),
		});
	};

	// Handle add tag to action item
	const handleAddActionItemTag = () => {
		if (
			actionItemNewTag.trim() &&
			!actionItemFormData.tags.includes(actionItemNewTag.trim())
		) {
			setActionItemFormData({
				...actionItemFormData,
				tags: [actionItemNewTag.trim(), ...actionItemFormData.tags],
			});
			setActionItemNewTag("");
		}
	};

	// Handle remove tag from action item
	const handleRemoveActionItemTag = (tagToRemove: string) => {
		setActionItemFormData({
			...actionItemFormData,
			tags: actionItemFormData.tags.filter((tag) => tag !== tagToRemove),
		});
	};

	// Handle cycle status
	const handleCycleStatus = async (index: number) => {
		const item = actionItems[index];
		if (!item || !user?.companyId || !item.id) return;

		const nextStatus = (
			item.status === "open"
				? "in-progress"
				: item.status === "in-progress"
					? "closed"
					: "open"
		) as "open" | "in-progress" | "closed";

		try {
			// Optimistic update
			const updatedActionItems = actionItems.map((a, i) =>
				i === index ? { ...a, status: nextStatus } : a,
			);
			setActionItems(updatedActionItems);

			await updateActionItem(
				db,
				user.companyId!,
				item.id,
				{
					status: nextStatus,
				},
				user.uid,
			);
		} catch (error) {
			console.error("Error updating action item status:", error);
			Sentry.captureException(error);
			toast.error("Failed to update status");
			// Revert on error
			const revertedActionItems = actionItems.map((a, i) =>
				i === index ? { ...a, status: item.status } : a,
			);
			setActionItems(revertedActionItems);
		}
	};

	// Handle mark all action items as completed
	const handleMarkAllAsCompleted = async () => {
		if (!user?.companyId) return;

		const itemsToClose = actionItems.filter(
			(item) => item.id && item.status !== "closed",
		);
		if (itemsToClose.length === 0) return;

		const previousActionItems = actionItems;
		const updatedActionItems = actionItems.map((item) => ({
			...item,
			status: "closed" as const,
		}));
		setActionItems(updatedActionItems);

		const results = await Promise.allSettled(
			itemsToClose.map((item) =>
				updateActionItem(
					db,
					user.companyId!,
					item.id!,
					{
						status: "closed",
					},
					user.uid,
				),
			),
		);

		const failedIds = itemsToClose
			.filter((_, index) => results[index]?.status === "rejected")
			.map((item) => item.id)
			.filter((id): id is string => Boolean(id));

		if (failedIds.length > 0) {
			const previousById = new Map(
				previousActionItems.map((item) => [item.id, item]),
			);
			const failedErrors = results
				.filter((result) => result.status === "rejected")
				.map((result) => (result as PromiseRejectedResult).reason);

			console.error("Error updating action items:", failedErrors);
			failedErrors.forEach((error) => Sentry.captureException(error));
			toast.error("Failed to mark all items as completed");
			setActionItems(
				updatedActionItems.map((item) =>
					item.id && failedIds.includes(item.id)
						? (previousById.get(item.id) ?? item)
						: item,
				),
			);
		}
	};

	// Get background color based on severity and status
	const getBackgroundColor = (item: ActionItemState) => {
		if (item.status === "closed") {
			return "bg-muted";
		}
		switch (item.priority) {
			case "urgent":
				return "bg-danger-tint";
			case "normal":
				return "";
			case "low":
				return "bg-accent";
			default:
				return "";
		}
	};

	// Handle download transcript
	const handleDownloadTranscript = () => {
		if (!formattedTranscript) return;

		const collectedNumbersText =
			collectedNumbers.length > 0
				? `Collected numbers:\n${collectedNumbers
						.map(
							(item) =>
								`${item.label}: ${
									item.kind === "phone_number"
										? formatPhoneNumber(item.value)
										: item.value
								}`,
						)
						.join("\n")}\n\n---\n\n`
				: "";
		const participantLabel = isOutbound ? "Callee" : "Caller";
		const metadata = `${participantLabel}: ${contactDisplayName}\nPhone: ${formatPhoneNumber(contactDisplayPhone)}\nDate: ${call.date}\nTime: ${call.time}\n\n---\n\n${collectedNumbersText}`;
		const content = metadata + formattedTranscript;

		const element = document.createElement("a");
		const file = new Blob([content], { type: "text/plain" });
		element.href = URL.createObjectURL(file);
		element.download = `transcript-${call.id}.txt`;
		document.body.appendChild(element); // Required for this to work in FireFox
		element.click();
		document.body.removeChild(element);
	};

	// Scroll to section
	const scrollToSection = (section: string) => {
		const ref = {
			summary: summaryRef,
			actionItems: actionItemsRef,
			transcript: transcriptRef,
			callerDetails: callerDetailsRef,
		}[section];

		if (ref?.current) {
			const element = ref.current;
			const elementRect = element.getBoundingClientRect();
			const absoluteElementTop = elementRect.top + window.pageYOffset;
			const offset = window.innerWidth < 768 ? 80 : 20;

			window.scrollTo({
				top: absoluteElementTop - offset,
				behavior: "smooth",
			});
		}
	};

	// Check tab bar scroll state
	const updateTabBarScrollState = useCallback(() => {
		if (tabBarRef.current) {
			const {
				scrollLeft: rawScrollLeft,
				scrollWidth,
				clientWidth,
			} = tabBarRef.current;
			const scrollLeft = Math.abs(rawScrollLeft);
			const outputIsRTL = language === "he";
			const maxScroll = scrollWidth - clientWidth;

			if (outputIsRTL) {
				// RTL: Origin is Right (0). Left is Max (Negative or Positive depending on browser).
				// Left Button (Physically Left) moves AWAY from Origin (Right).
				// So Left Button is enabled if we are not at Max.
				setCanScrollLeft(scrollLeft < maxScroll - 5);

				// Right Button (Physically Right) moves TOWARDS Origin (Right).
				// So Right Button is enabled if we are not at 0.
				setCanScrollRight(scrollLeft > 5);
			} else {
				// LTR: Origin is Left (0). Right is Max.
				// Left Button moves TOWARDS Origin.
				setCanScrollLeft(scrollLeft > 5);
				// Right Button moves AWAY from Origin.
				setCanScrollRight(scrollLeft < maxScroll - 5);
			}
		}
	}, [language]);
	// Scroll tab bar left/right
	const scrollTabBar = (direction: "left" | "right") => {
		if (tabBarRef.current) {
			const scrollAmount = 150;
			tabBarRef.current.scrollBy({
				left: direction === "left" ? -scrollAmount : scrollAmount,
				behavior: "smooth",
			});
		}
	};

	// Set up intersection observer for sections
	useEffect(() => {
		const options = {
			root: null,
			rootMargin: "-120px 0px -70% 0px",
			threshold: 0,
		};

		const callback = (entries: IntersectionObserverEntry[]) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const sectionId = entry.target.getAttribute("data-section");
					if (sectionId) {
						setActiveSection(sectionId);
					}
				}
			});
		};

		const observer = new IntersectionObserver(callback, options);

		[summaryRef, actionItemsRef, transcriptRef, callerDetailsRef].forEach(
			(ref) => {
				if (ref.current) {
					observer.observe(ref.current);
				}
			},
		);

		return () => observer.disconnect();
	}, []);

	// Update tab bar scroll state on mount and when active section changes
	// biome-ignore lint/correctness/useExhaustiveDependencies: Active-section changes alter the rendered tab geometry and require scroll-state recalculation.
	useEffect(() => {
		updateTabBarScrollState();
		const currentTabBar = tabBarRef.current;
		if (currentTabBar) {
			currentTabBar.addEventListener("scroll", updateTabBarScrollState);
			return () => {
				currentTabBar.removeEventListener("scroll", updateTabBarScrollState);
			};
		}
	}, [activeSection, updateTabBarScrollState]);

	const isCallNavigationEnabled =
		shortcutsEnabled &&
		!isTranscriptEditOpen &&
		!isEditDialogOpen &&
		!isEditCallInfoOpen &&
		!isEditActionItemOpen &&
		!isCreateActionItemOpen;

	return (
		<div
			className="max-w-7xl mx-auto px-4 space-y-6 pb-20"
			data-testid="call-detail"
		>
			<div className="flex flex-wrap items-center justify-between gap-3">
				<Button
					variant="ghost"
					onClick={onBack}
					data-testid="call-detail-back"
					aria-label={t({ id: "backToCalls", message: "Back to Calls" })}
				>
					{language === "he" ? (
						<ArrowRight className="w-4 h-4 ml-2" />
					) : (
						<ArrowLeft className="w-4 h-4 mr-2" />
					)}
					{t({ id: "backToCalls", message: "Back to Calls" })}
				</Button>
				<CallNavigationControls
					previousCallId={previousCallId}
					nextCallId={nextCallId}
					onNavigateToCall={onNavigateToCall}
					shortcutsEnabled={isCallNavigationEnabled}
				/>
			</div>

			{/* Mobile Action Bar */}
			<div
				className="sticky top-0 bg-card border-b shadow-sm z-40 md:hidden"
				dir="ltr"
			>
				<div className="flex items-center px-2 py-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => scrollTabBar("left")}
						disabled={!canScrollLeft}
						className="flex-shrink-0"
					>
						<ChevronLeft className="w-4 h-4" />
					</Button>
					<div
						ref={tabBarRef}
						dir={language === "he" ? "rtl" : "ltr"}
						className="flex-1 overflow-x-auto scrollbar-hide flex items-center gap-2 px-1"
						style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
					>
						<Button
							variant={activeSection === "summary" ? "default" : "ghost"}
							size="sm"
							onClick={() => scrollToSection("summary")}
							className="flex-shrink-0 text-xs"
						>
							{t({ id: "summary", message: "Summary" })}
						</Button>
						<Button
							variant={activeSection === "actionItems" ? "default" : "ghost"}
							size="sm"
							onClick={() => scrollToSection("actionItems")}
							className="flex-shrink-0 text-xs"
						>
							{t({ id: "actionItems", message: "Action Items" })}
						</Button>
						<Button
							variant={activeSection === "transcript" ? "default" : "ghost"}
							size="sm"
							onClick={() => scrollToSection("transcript")}
							className="flex-shrink-0 text-xs"
						>
							{t({ id: "transcript", message: "Transcript" })}
						</Button>
						<Button
							variant={activeSection === "callerDetails" ? "default" : "ghost"}
							size="sm"
							onClick={() => scrollToSection("callerDetails")}
							className="flex-shrink-0 text-xs"
						>
							{isOutbound
								? t({ id: "calleeDetails", message: "Callee Details" })
								: t({ id: "callerDetails", message: "Caller Details" })}
						</Button>
					</div>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => scrollTabBar("right")}
						disabled={!canScrollRight}
						className="flex-shrink-0"
					>
						<ChevronRight className="w-4 h-4" />
					</Button>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Call Information */}
				<div className="flex flex-col gap-6 lg:col-span-2">
					{/* Summary */}
					{/* Wrapper div keeps a real DOM ref for scroll/observer tracking (Card doesn't forward refs). */}
					<div ref={summaryRef} data-section="summary" className="order-1">
						<Card>
							<CardHeader className="border-b border-border">
								<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
									<div className="flex min-w-0 flex-col gap-2">
										<CardTitle className="flex items-center space-x-2 rtl:space-x-reverse">
											<FileText className="w-5 h-5" />
											<span>
												{t({ id: "callSummary", message: "Call Summary" })}
											</span>
										</CardTitle>
										{call.title && (
											<p
												className="text-sm font-semibold break-words [overflow-wrap:anywhere]"
												dir="auto"
											>
												{call.title}
											</p>
										)}
									</div>
									<div className="flex items-center gap-1 self-start lg:self-auto">
										<Button
											variant="ghost"
											size="sm"
											onClick={handleEditCallInfoOpen}
											aria-label={t({
												id: "editCallInfo",
												message: "Edit Call Info",
											})}
											data-testid="call-detail-edit-call-info"
										>
											<Pencil className="w-4 h-4" />
										</Button>
										{call.interactionStatus === "read" && (
											<Button
												variant="ghost"
												size="sm"
												onClick={handleMarkUnread}
												title={t({
													id: "markAsUnread",
													message: "Mark as unread",
												})}
												aria-label={t({
													id: "markAsUnread",
													message: "Mark as unread",
												})}
												data-testid="call-detail-mark-unread"
											>
												<Mail className="w-4 h-4" />
											</Button>
										)}
										<CallArchiveButton
											isArchived={call.interactionStatus === "archived"}
											onClick={handleToggleArchive}
										/>
									</div>
								</div>
							</CardHeader>
							<div className="flex flex-wrap items-center gap-2 border-b border-border px-6 pb-5">
								<Badge
									variant="outline"
									className="border-border bg-secondary text-xs font-normal text-muted-foreground"
								>
									<Calendar className="mr-1 h-3 w-3 rtl:mr-0 rtl:ml-1" />
									<span>
										{call.date} {t({ id: "at", message: "at" })}{" "}
										{formattedCallTime}
									</span>
								</Badge>
								<Badge
									variant="outline"
									className="border-border bg-secondary text-xs font-normal text-muted-foreground"
								>
									<Clock className="mr-1 h-3 w-3 rtl:mr-0 rtl:ml-1" />
									<span>{call.duration || "N/A"}</span>
								</Badge>
								<Tooltip delayDuration={2000}>
									<TooltipTrigger asChild>
										<Badge
											variant="outline"
											className="max-w-full cursor-help border-border bg-secondary text-xs font-normal text-muted-foreground"
										>
											<Phone className="mr-1 h-3 w-3 rtl:mr-0 rtl:ml-1" />
											<span>Case ID: {formatCaseId(call.caseId)}</span>
										</Badge>
									</TooltipTrigger>
									<TooltipContent
										side="top"
										sideOffset={6}
										className="max-w-xs text-left"
									>
										<div>Case ID: {formatCaseId(call.caseId)}</div>
										<div>Call ID: {call.id}</div>
									</TooltipContent>
								</Tooltip>
								{call.callType === "human" && (
									<Badge
										variant="outline"
										className="h-5 shrink-0 border-warn/30 bg-warn-tint px-2 text-[11px] font-semibold text-foreground"
									>
										<PhoneForwarded className="w-3 h-3" />
										{t({
											id: "humanForwardedBadge",
											message: "Forwarded to Operator",
										})}
									</Badge>
								)}
								{call.isTestCall && (
									<Badge
										variant="outline"
										className="h-5 shrink-0 border-ring/30 bg-accent px-2 text-[11px] font-semibold text-accent-foreground"
									>
										<FlaskConical className="w-3 h-3" />
										{t({ id: "testNumberBadge", message: "Test Number" })}
									</Badge>
								)}
								{callTags.map((tag) => (
									<Badge
										key={tag.id}
										variant="secondary"
										className={`text-xs ${getServiceTagClassName(tag.color, { deleted: Boolean(tag.deletedAt) })}`}
									>
										{tag.label}
									</Badge>
								))}
							</div>
							<CardContent className="space-y-4">
								<p
									className={`text-sm leading-relaxed text-muted-foreground break-words [overflow-wrap:anywhere] ${shouldShowStatusFallback ? "text-muted-foreground italic" : ""}`}
									dir="auto"
								>
									{shouldShowStatusFallback ? fallbackSummary : call.summary}
								</p>
								{outboundContext?.isOutbound && (
									<div className="rounded-lg border border-ring/30 bg-accent p-4">
										<div className="mb-3 flex flex-wrap items-center gap-2">
											<Badge className="border border-ring/30 bg-accent text-accent-foreground">
												{t({ id: "outboundBadge", message: "Outbound" })}
											</Badge>
											{outboundContext.playbookName && (
												<span className="text-sm font-semibold text-accent-ink">
													{outboundContext.playbookName}
												</span>
											)}
											{outboundContext.outcome && (
												<Badge variant="outline" className="bg-card">
													{i18n._({
														id: "outboundOutcomeBadge",
														message: "Outcome: {outcome}",
														values: { outcome: outboundContext.outcome },
													})}
												</Badge>
											)}
											{outboundContext.taskStatus && (
												<Badge variant="outline" className="bg-card">
													{i18n._({
														id: "outboundTaskBadge",
														message: "Task: {status}",
														values: { status: outboundContext.taskStatus },
													})}
												</Badge>
											)}
										</div>
										<div className="grid gap-3 text-sm md:grid-cols-2">
											<div>
												<div className="text-xs font-medium text-accent-ink">
													{t({ id: "outboundGoalHeader", message: "Goal" })}
												</div>
												<div className="mt-1 text-accent-ink">
													{outboundContext.playbook?.description ||
														outboundContext.playbookName ||
														t({
															id: "outboundPlaybookCallFallback",
															message: "Outbound playbook call",
														})}
												</div>
											</div>
											<div>
												<div className="text-xs font-medium text-accent-ink">
													{t({
														id: "outboundAttemptsHeader",
														message: "Attempts",
													})}
												</div>
												<div className="mt-1 text-accent-ink">
													{outboundContext.attemptNumber
														? i18n._({
																id: "outboundAttemptNumber",
																message: "Attempt {attempt}",
																values: {
																	attempt: outboundContext.attemptNumber,
																},
															})
														: t({
																id: "outboundAttemptUnavailable",
																message: "Attempt details unavailable",
															})}
													{outboundContext.maxAttempts
														? i18n._({
																id: "outboundMaxAttempts",
																message: " of {maxAttempts}",
																values: {
																	maxAttempts: outboundContext.maxAttempts,
																},
															})
														: ""}
												</div>
											</div>
										</div>
										{outboundContext.summary && (
											<div className="mt-3 text-sm text-accent-ink">
												<span className="font-medium">
													{t({
														id: "outboundTaskSummaryLabel",
														message: "Task summary:",
													})}{" "}
												</span>
												{outboundContext.summary}
											</div>
										)}
										{outboundInputEntries.length > 0 && (
											<div className="mt-3">
												<div className="mb-1 text-xs font-medium text-accent-ink">
													{t({
														id: "outboundTaskInputHeader",
														message: "Task input",
													})}
												</div>
												<div className="grid gap-2 md:grid-cols-2">
													{outboundInputEntries.map((entry) => (
														<div
															key={entry.key}
															className="rounded-md border border-ring/30 bg-card px-3 py-2"
														>
															<div className="text-[11px] text-muted-foreground">
																{entry.label}
															</div>
															<div
																className="truncate text-sm text-foreground"
																dir={isPhoneKey(entry.key) ? "ltr" : undefined}
															>
																{isPhoneKey(entry.key)
																	? formatPhoneNumber(entry.value)
																	: entry.value}
															</div>
														</div>
													))}
												</div>
											</div>
										)}
										{outboundResultEntries.length > 0 && (
											<div className="mt-3">
												<div className="mb-1 text-xs font-medium text-accent-ink">
													{t({
														id: "outboundResultHeader",
														message: "Result",
													})}
												</div>
												<div className="grid gap-2 md:grid-cols-2">
													{outboundResultEntries.map((entry) => (
														<div
															key={entry.key}
															className="rounded-md border border-ring/30 bg-card px-3 py-2"
														>
															<div className="text-[11px] text-muted-foreground">
																{entry.label}
															</div>
															<div
																className="truncate text-sm text-foreground"
																dir={isPhoneKey(entry.key) ? "ltr" : undefined}
															>
																{isPhoneKey(entry.key)
																	? formatPhoneNumber(entry.value)
																	: entry.value}
															</div>
														</div>
													))}
												</div>
											</div>
										)}
									</div>
								)}
								{callNotes ? (
									<div className="rounded-lg border bg-secondary p-3">
										<p className="mb-1 text-xs font-medium text-muted-foreground">
											{t({ id: "notes", message: "Notes" })}
										</p>
										<p
											className="text-sm text-muted-foreground whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
											dir="auto"
											style={{ whiteSpace: "pre-wrap" }}
										>
											{callNotes}
										</p>
									</div>
								) : null}
							</CardContent>
						</Card>
					</div>

					{/* Action Items */}
					<div
						ref={actionItemsRef}
						data-section="actionItems"
						className="order-2"
					>
						<Card>
							<CardHeader className="border-b border-border">
								<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
									<div>
										<CardTitle className="flex items-center space-x-2 rtl:space-x-reverse">
											<CheckCircle className="w-5 h-5" />
											<span>
												{t({ id: "actionItems", message: "Action Items" })}
											</span>
										</CardTitle>
										<CardDescription className="mt-1.5">
											{t({
												id: "followUpTasks",
												message: "Follow-up tasks from recent interactions",
											})}
										</CardDescription>
									</div>
									<div className="flex items-center gap-1 self-start lg:self-auto">
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													variant="outline"
													size="icon"
													className="h-8 w-8"
													onClick={handleCreateActionItemOpen}
												>
													<Plus className="w-4 h-4" />
												</Button>
											</TooltipTrigger>
											<TooltipContent side="top">
												{t({
													id: "addActionItem",
													message: "Add Action Item",
												})}
											</TooltipContent>
										</Tooltip>
									</div>
								</div>
							</CardHeader>
							<CardContent>
								<div className="space-y-3">
									{actionItems.map((item, index) => (
										<div
											key={index}
											className={`flex items-start space-x-3 rtl:space-x-reverse p-4 ${getBackgroundColor(item)} rounded-lg border-[1.5px] border-card-border shadow-card hover:border-ring transition-colors`}
											style={{
												backgroundColor:
													item.priority === "normal" && item.status !== "closed"
														? "lightyellow"
														: undefined,
											}}
										>
											<div className="flex-1 space-y-2">
												{/* Title and Status */}
												<div className="flex items-start justify-between">
													<h4 className="text-sm text-foreground font-medium">
														{item.title}
													</h4>
													<Badge
														variant={
															item.status === "open"
																? "secondary"
																: item.status === "in-progress"
																	? "default"
																	: "outline"
														}
														className="text-xs ml-2 rtl:ml-0 rtl:mr-2 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1"
														onClick={() => handleCycleStatus(index)}
													>
														{/* Icon based on status */}
														{item.status === "open" && (
															<span className="mr-1">○</span>
														)}
														{item.status === "in-progress" && (
															<span className="mr-1">⟳</span>
														)}
														{item.status === "closed" && (
															<span className="mr-1">✓</span>
														)}
														{item.status === "open" &&
															t({ id: "status.open", message: "Open" })}
														{item.status === "in-progress" &&
															t({
																id: "status.in-progress",
																message: "In Progress",
															})}
														{item.status === "closed" &&
															t({ id: "status.closed", message: "Closed" })}
														<ChevronDown className="w-3 h-3 ml-1 opacity-50" />
													</Badge>
												</div>
												{/* Description */}
												<p
													className="text-sm text-muted-foreground whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
													dir="auto"
													style={{ whiteSpace: "pre-wrap" }}
												>
													{item.description}
												</p>
												{/* Notes (if present) */}
												{item.notes && (
													<div className="flex items-start space-x-2 rtl:space-x-reverse pt-2 border-t border-border">
														<StickyNote className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
														<p
															className="text-xs text-muted-foreground italic whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
															dir="auto"
															style={{ whiteSpace: "pre-wrap" }}
														>
															{item.notes}
														</p>
													</div>
												)}
												{/* Tags */}
												{item.tags && item.tags.length > 0 && (
													<div className="flex items-center flex-wrap gap-2 pt-1">
														<Tag className="w-3 h-3 text-muted-foreground flex-shrink-0" />
														{item.tags.map((tag, tagIndex) => (
															<Badge
																key={tagIndex}
																variant="outline"
																className="text-xs"
															>
																{tag}
															</Badge>
														))}
													</div>
												)}
											</div>
											<div className="flex items-start space-x-1 rtl:space-x-reverse">
												<Button
													variant="ghost"
													size="sm"
													onClick={() => handleEditActionItemOpen(index)}
													className="flex-shrink-0"
												>
													<Pencil className="w-4 h-4" />
												</Button>
											</div>
										</div>
									))}
									{actionItems.length === 0 && (
										<div className="text-center py-4 text-muted-foreground text-sm">
											{t({ id: "noActionItems", message: "No action items" })}
										</div>
									)}
								</div>
								{actionItems.length > 0 && (
									<Button
										className="w-full mt-4"
										variant="outline"
										onClick={handleMarkAllAsCompleted}
									>
										{t({
											id: "markAllComplete",
											message: "Mark All Complete",
										})}
									</Button>
								)}
							</CardContent>
						</Card>
					</div>

					{/* Sent Messages */}
					{smsLogs.length > 0 && (
						<div data-section="sentMessages" className="order-4">
							<Card>
								<CardHeader className="border-b border-border">
									<CardTitle className="flex items-center space-x-2 rtl:space-x-reverse">
										<MessageCircle className="w-5 h-5" />
										<span>
											{language === "he" ? "הודעות שנשלחו" : "Sent Messages"}
										</span>
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="space-y-3">
										{smsLogs.map((log, index) => (
											<div
												key={index}
												className="flex flex-col space-y-1 rounded-lg border border-border bg-secondary p-3"
											>
												<div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
													<span className="text-xs font-semibold text-muted-foreground break-all">
														From:{" "}
														<bdi dir="ltr">{formatPhoneNumber(log.from)}</bdi>
														{" → "}To:{" "}
														<bdi dir="ltr">{formatPhoneNumber(log.to)}</bdi>
													</span>
													{log.timestamp && (
														<span className="text-xs text-muted-foreground">
															{log.timestamp.toDate
																? log.timestamp.toDate().toLocaleTimeString()
																: ""}
														</span>
													)}
												</div>
												<p
													className="text-sm text-foreground whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
													dir="auto"
													style={{ whiteSpace: "pre-wrap" }}
												>
													{log.body}
												</p>
											</div>
										))}
									</div>
								</CardContent>
							</Card>
						</div>
					)}

					{/* Transcript */}
					<div
						ref={transcriptRef}
						data-section="transcript"
						className="order-3"
					>
						<Card>
							<CardHeader className="border-b border-border">
								<CardTitle className="flex items-center justify-between">
									<span>{t({ id: "transcript", message: "Transcript" })}</span>
									<div className="flex items-center space-x-2 rtl:space-x-reverse">
										<Button
											variant="ghost"
											size="sm"
											onClick={handlePlayRecording}
											aria-label={t({ id: "playAudio", message: "Play audio" })}
											data-testid="call-detail-play"
										>
											<Play className="w-4 h-4" />
										</Button>
										<Button
											variant="ghost"
											size="sm"
											onClick={handleDownloadTranscript}
											disabled={!transcriptText}
											aria-label={t({
												id: "downloadTranscript",
												message: "Download transcript",
											})}
											data-testid="call-detail-download"
										>
											<Download className="w-4 h-4" />
										</Button>
										<Button
											variant="ghost"
											size="sm"
											onClick={handleTranscriptEditOpen}
											disabled={
												!transcriptText || !user?.companyId || !user.uid
											}
											aria-label={t({
												id: "transcriptEdit.action",
												message: "Edit transcript",
											})}
											data-testid="call-detail-edit-transcript"
										>
											<Pencil className="w-4 h-4" />
										</Button>
									</div>
								</CardTitle>
								<CardDescription>
									{t({
										id: "autoTranscript",
										message:
											"Auto-generated transcript from the call recording",
									})}
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="bg-secondary p-4 rounded-lg">
									{collectedNumbers.length > 0 && (
										<div className="mb-4 border-b border-border pb-4">
											<div className="mb-2 text-xs font-semibold text-muted-foreground">
												{t({
													id: "collectedNumbers",
													message: "Collected numbers",
												})}
											</div>
											<div className="flex flex-wrap gap-2">
												{collectedNumbers.map((item, index) => (
													<Badge
														key={`${item.kind}-${index}`}
														variant="secondary"
														className="gap-2 font-mono"
													>
														<span className="font-sans font-medium">
															{item.label}
														</span>
														<span dir="ltr">
															{item.kind === "phone_number"
																? formatPhoneNumber(item.value)
																: item.value}
														</span>
													</Badge>
												))}
											</div>
										</div>
									)}
									<div className="space-y-3">
										{formattedTranscript ? (
											formattedTranscript
												.split("\n")
												.map((line: string, index: number) => {
													const isAgent =
														line.startsWith("Agent:") ||
														line.startsWith("סוכן:");
													const isCaller =
														line.startsWith("Caller:") ||
														line.startsWith("מתקשר:");

													return (
														<div
															key={index}
															className={`${isAgent ? "text-accent-ink" : isCaller ? "text-foreground" : "text-muted-foreground"}`}
														>
															<p
																className="text-sm leading-relaxed break-words [overflow-wrap:anywhere]"
																dir="auto"
															>
																{line}
															</p>
														</div>
													);
												})
										) : (
											<p className="text-muted-foreground italic text-center flex items-center justify-center gap-2">
												{isProcessingCall && (
													<Loader2 className="h-4 w-4 animate-spin" />
												)}
												{isFailedWithoutTranscript && (
													<AlertCircle className="h-4 w-4" />
												)}
												{transcriptFallback}
											</p>
										)}
									</div>
								</div>
								{formattedTranscript && (
									<div className="mt-3 flex justify-end">
										<TranscriptFeedback
											value={transcriptFeedback}
											language={language as "en" | "he"}
											disabled={!user?.companyId || !user.uid}
											onSubmit={handleTranscriptFeedback}
										/>
									</div>
								)}
							</CardContent>
						</Card>
					</div>
				</div>

				{/* Sidebar */}
				<div className="space-y-6">
					{contact && (
						<a
							className="block text-sm text-primary underline underline-offset-4"
							href={`?page=callers&callerId=${encodeURIComponent(contact.id!)}`}
						>
							{t({ id: "viewCallerProfile", message: "View caller profile" })}
						</a>
					)}
					{/* Caller Details */}
					<div ref={callerDetailsRef} data-section="callerDetails">
						<Card>
							<CardHeader className="border-b border-border">
								<CardTitle className="flex items-center justify-between">
									<div className="flex items-center space-x-2 rtl:space-x-reverse">
										<User className="w-5 h-5" />
										<span>
											{isOutbound
												? t({ id: "calleeDetails", message: "Callee Details" })
												: t({ id: "callerDetails", message: "Caller Details" })}
										</span>
									</div>
									<Button
										variant="ghost"
										size="sm"
										onClick={handleEditOpen}
										aria-label={
											isOutbound
												? t({
														id: "editCalleeInfo",
														message: "Edit Callee Info",
													})
												: t({
														id: "editCallerInfo",
														message: "Edit Caller Info",
													})
										}
										data-testid="call-detail-edit-contact"
									>
										<Pencil className="w-4 h-4" />
									</Button>
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-4">
									<div className="flex items-start gap-3">
										<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent">
											<User className="w-5 h-5 text-accent-ink" />
										</div>
										<div className="min-w-0 flex-1 space-y-3">
											<div className="space-y-1">
												<h3
													className="text-base font-medium leading-none"
													dir="auto"
												>
													{contactDisplayName}
												</h3>
											</div>
											<div className="space-y-2">
												<div className="flex items-center gap-3">
													<Phone className="w-4 h-4 text-muted-foreground" />
													<span className="text-sm" dir="ltr">
														{formatPhoneNumber(contactDisplayPhone)}
													</span>
												</div>
												{call.caller.email && (
													<div className="flex items-center gap-3">
														<Mail className="w-4 h-4 text-muted-foreground" />
														<span className="text-sm break-all">
															{call.caller.email}
														</span>
													</div>
												)}
												{call.caller.tags && call.caller.tags.length > 0 && (
													<div className="flex items-start gap-3">
														<Tag className="w-4 h-4 text-muted-foreground mt-0.5" />
														<div className="flex flex-wrap gap-1.5">
															{call.caller.tags.map(
																(tag: string, index: number) => (
																	<Badge
																		key={index}
																		variant="secondary"
																		className="text-xs"
																	>
																		{tag}
																	</Badge>
																),
															)}
														</div>
													</div>
												)}
											</div>
											{call.caller.notes ? (
												<div className="rounded-lg border bg-secondary p-3">
													<h4 className="mb-1 text-xs font-medium text-muted-foreground">
														{t({ id: "notes", message: "Notes" })}
													</h4>
													<p
														className="text-sm text-muted-foreground whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
														dir="auto"
														style={{ whiteSpace: "pre-wrap" }}
													>
														{call.caller.notes}
													</p>
												</div>
											) : null}
										</div>
									</div>

									<div className="pt-4 border-t">
										<h4 className="text-sm text-muted-foreground mb-2">
											{t({ id: "callHistory", message: "Call History" })}
										</h4>
										<div className="space-y-2">
											<div className="text-xs bg-secondary p-2 rounded">
												<p className="text-muted-foreground">
													{t({ id: "lastCall", message: "Last call" })}:{" "}
													{lastCall ? lastCall.date : "N/A"}
												</p>
											</div>
											{firstCall && firstCall.id !== lastCall?.id && (
												<div className="text-xs bg-secondary p-2 rounded">
													<p className="text-muted-foreground">
														{t({ id: "firstCall", message: "First call" })}:{" "}
														{firstCall.date}
													</p>
												</div>
											)}
											<div className="mt-3 max-h-64 overflow-y-auto">
												<p className="text-xs text-muted-foreground mb-2">
													{t({ id: "recentCalls", message: "Recent Calls" })} (
													{callerHistory.length}):
												</p>
												<div className="space-y-1">
													{callerHistory.slice(0, 5).map((historyCall) => (
														<div
															key={historyCall.id}
															onClick={() => onNavigateToCall?.(historyCall.id)}
															className={`text-xs p-2 rounded border-[1.5px] border-card-border shadow-card cursor-pointer transition-colors ${historyCall.id === call.id ? "bg-muted" : "bg-card hover:bg-accent"}`}
														>
															<div className="flex items-center justify-between space-x-2 rtl:space-x-reverse">
																<span className="text-foreground">
																	{historyCall.date}
																</span>
																<Clock className="w-3 h-3 text-muted-foreground" />
															</div>
															<p
																className="text-muted-foreground mt-1 truncate"
																dir="auto"
															>
																{historyCall.summary
																	? historyCall.summary.substring(0, 50) +
																		(historyCall.summary.length > 50
																			? "..."
																			: "")
																	: t({
																			id: "noSummary",
																			message: "No summary available",
																		})}
															</p>
														</div>
													))}
												</div>
											</div>
										</div>
									</div>

									{user?.companyId && (
										<div className="mt-4">
											<CallerMemoryCard
												companyId={user.companyId}
												phoneNumber={contactDisplayPhone || call.caller.phone}
												language={language as "en" | "he"}
												isOutbound={isOutbound}
											/>
											<CallerInstructionsCard
												companyId={user.companyId}
												phoneNumber={contactDisplayPhone || call.caller.phone}
												language={language as "en" | "he"}
												isOutbound={isOutbound}
											/>
										</div>
									)}
								</div>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>

			<Dialog
				open={isTranscriptEditOpen}
				onOpenChange={(open) =>
					!isSavingTranscriptEdit && setIsTranscriptEditOpen(open)
				}
			>
				<DialogContent className="sm:max-w-[700px]">
					<DialogHeader>
						<DialogTitle>
							{t({
								id: "transcriptEdit.title",
								message: "Edit transcript",
							})}
						</DialogTitle>
						<DialogDescription>
							{t({
								id: "transcriptEdit.description",
								message:
									"Correct transcription errors. The original transcript will be retained.",
							})}
						</DialogDescription>
					</DialogHeader>
					<Textarea
						value={transcriptEditText}
						onChange={(event) => setTranscriptEditText(event.target.value)}
						rows={18}
						{...contentFieldProps(transcriptEditText, "font-mono text-sm")}
					/>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsTranscriptEditOpen(false)}
							disabled={isSavingTranscriptEdit}
						>
							{t({ id: "cancel", message: "Cancel" })}
						</Button>
						<Button
							onClick={handleTranscriptEditSave}
							disabled={
								isSavingTranscriptEdit ||
								!transcriptEditText.trim() ||
								transcriptEditText === transcriptText
							}
						>
							{isSavingTranscriptEdit
								? t({ id: "saving", message: "Saving..." })
								: t({ id: "save", message: "Save" })}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Edit Caller Dialog */}
			<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
				<DialogContent className="sm:max-w-[500px]">
					<DialogHeader>
						<DialogTitle>
							{isOutbound
								? t({ id: "editCalleeInfo", message: "Edit Callee Info" })
								: t({ id: "editCallerInfo", message: "Edit Caller Info" })}
						</DialogTitle>
						<DialogDescription>
							{t({
								id: "makeChanges",
								message: "Make changes to your agent configuration",
							})}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="caller-name">
								{isOutbound
									? t({ id: "calleeName", message: "Callee Name" })
									: t({ id: "callerName", message: "Caller Name" })}
							</Label>
							<Input
								id="caller-name"
								value={editFormData.name}
								onChange={(e) =>
									setEditFormData({ ...editFormData, name: e.target.value })
								}
								{...contentFieldProps(editFormData.name)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="caller-email">
								{t({ id: "emailAddress", message: "Email Address" })}
							</Label>
							<Input
								id="caller-email"
								type="email"
								dir="ltr"
								className="text-left"
								value={editFormData.email}
								onChange={(e) =>
									setEditFormData({ ...editFormData, email: e.target.value })
								}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="caller-phone">
								{t({ id: "phoneNumber", message: "Phone Number" })}
							</Label>
							<Input
								id="caller-phone"
								value={formatPhoneNumber(
									contactDisplayPhone || call.caller.phone,
								)}
								disabled
								className="bg-muted cursor-not-allowed"
								dir="ltr"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="caller-notes">
								{t({ id: "notes", message: "Notes" })}
							</Label>
							<Textarea
								id="caller-notes"
								value={editFormData.notes}
								onChange={(e) =>
									setEditFormData({ ...editFormData, notes: e.target.value })
								}
								rows={3}
								placeholder={t({ id: "noNotes", message: "No notes" })}
								{...contentFieldProps(editFormData.notes)}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t({ id: "tags", message: "Tags" })}</Label>
							<div className="flex flex-wrap gap-2 mb-2">
								{editFormData.tags.map((tag, index) => (
									<Badge
										key={index}
										variant="secondary"
										className="text-xs flex items-center space-x-1 rtl:space-x-reverse"
									>
										<span>{tag}</span>
										<button
											onClick={() => handleRemoveTag(tag)}
											className="ml-1 rtl:ml-0 rtl:mr-1 hover:text-danger-ink"
										>
											<X className="w-3 h-3" />
										</button>
									</Badge>
								))}
							</div>
							<div className="flex space-x-2 rtl:space-x-reverse">
								<Input
									value={newTag}
									onChange={(e) => setNewTag(e.target.value)}
									placeholder={t({ id: "addTag", message: "Add Tag" })}
									onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
									{...contentFieldProps(newTag)}
								/>
								<Button type="button" onClick={handleAddTag} size="sm">
									{t({ id: "addTag", message: "Add Tag" })}
								</Button>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsEditDialogOpen(false)}
						>
							{t({ id: "cancel", message: "Cancel" })}
						</Button>
						<Button onClick={handleSave}>
							{t({ id: "saveChanges", message: "Save Changes" })}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Edit Call Info Dialog */}
			<Dialog open={isEditCallInfoOpen} onOpenChange={setIsEditCallInfoOpen}>
				<DialogContent className="sm:max-w-[500px]">
					<DialogHeader>
						<DialogTitle>
							{t({ id: "editCallInfo", message: "Edit Call Info" })}
						</DialogTitle>
						<DialogDescription>
							{t({
								id: "makeChanges",
								message: "Make changes to your agent configuration",
							})}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="call-notes">
								{t({ id: "notes", message: "Notes" })}
							</Label>
							<Textarea
								id="call-notes"
								value={callInfoFormData.notes}
								onChange={(e) =>
									setCallInfoFormData({
										...callInfoFormData,
										notes: e.target.value,
									})
								}
								rows={4}
								placeholder={t({ id: "noNotes", message: "No notes" })}
								{...contentFieldProps(callInfoFormData.notes)}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t({ id: "tags", message: "Tags" })}</Label>
							<div className="flex flex-wrap gap-2 mb-2">
								{selectedCallInfoTags.map((tag) => (
									<Badge
										key={tag.id}
										variant="secondary"
										className={`text-xs flex items-center space-x-1 rtl:space-x-reverse ${getServiceTagClassName(tag.color, { deleted: Boolean(tag.deletedAt) })}`}
									>
										<span>{tag.label}</span>
										<button
											onClick={() => handleRemoveCallInfoTag(tag.id)}
											className="ml-1 rtl:ml-0 rtl:mr-1 hover:text-danger-ink"
										>
											<X className="w-3 h-3" />
										</button>
									</Badge>
								))}
							</div>
							{availableCallInfoTags.length > 0 ? (
								<Select
									value={callInfoTagIdToAdd ?? ""}
									onValueChange={handleSelectCallInfoTag}
								>
									<SelectTrigger className="h-9 min-w-0">
										<SelectValue
											placeholder={t({ id: "addTag", message: "Add Tag" })}
										/>
									</SelectTrigger>
									<SelectContent>
										{availableCallInfoTags.map((tag) => (
											<SelectItem key={tag.id} value={tag.id}>
												{tag.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							) : activeServiceTags.length > 0 ? (
								<p className="text-xs text-muted-foreground">
									No more active tags
								</p>
							) : (
								<p className="text-xs text-muted-foreground">No active tags</p>
							)}
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsEditCallInfoOpen(false)}
						>
							{t({ id: "cancel", message: "Cancel" })}
						</Button>
						<Button onClick={handleSaveCallInfo}>
							{t({ id: "saveChanges", message: "Save Changes" })}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Edit Action Item Dialog */}
			<Dialog
				open={isEditActionItemOpen}
				onOpenChange={setIsEditActionItemOpen}
			>
				<DialogContent className="sm:max-w-[500px]">
					<DialogHeader>
						<DialogTitle>
							{t({ id: "editActionItem", message: "Edit Action Item" })}
						</DialogTitle>
						<DialogDescription>
							{t({
								id: "editActionItemDescription",
								message: "Update this follow-up task",
							})}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="action-item-title">
								{t({ id: "title", message: "Title" })}
							</Label>
							<Input
								id="action-item-title"
								value={actionItemFormData.title}
								onChange={(e) =>
									setActionItemFormData({
										...actionItemFormData,
										title: e.target.value,
									})
								}
								{...contentFieldProps(actionItemFormData.title)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="action-item-description">
								{t({ id: "description", message: "Description" })}
							</Label>
							<Textarea
								id="action-item-description"
								value={actionItemFormData.description}
								onChange={(e) =>
									setActionItemFormData({
										...actionItemFormData,
										description: e.target.value,
									})
								}
								rows={2}
								{...contentFieldProps(actionItemFormData.description)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="action-item-notes">
								{t({ id: "notes", message: "Notes" })}
							</Label>
							<Textarea
								id="action-item-notes"
								value={actionItemFormData.notes}
								onChange={(e) =>
									setActionItemFormData({
										...actionItemFormData,
										notes: e.target.value,
									})
								}
								rows={3}
								placeholder={t({ id: "addNote", message: "Add Note" })}
								{...contentFieldProps(actionItemFormData.notes)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="action-item-status">
								{t({ id: "status", message: "Status" })}
							</Label>
							<Select
								value={actionItemFormData.status}
								onValueChange={(value) =>
									setActionItemFormData({
										...actionItemFormData,
										status: value as ActionItemState["status"],
									})
								}
							>
								<SelectTrigger className="w-[200px]">
									<SelectValue
										placeholder={t({
											id: "selectStatus",
											message: "Select Status",
										})}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="open">
										{t({ id: "open", message: "Open" })}
									</SelectItem>
									<SelectItem value="in-progress">
										{t({ id: "in-progress", message: "In Progress" })}
									</SelectItem>
									<SelectItem value="closed">
										{t({ id: "closed", message: "Closed" })}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="action-item-severity">
								{t({ id: "priority", message: "Priority" })}
							</Label>
							<Select
								value={actionItemFormData.priority}
								onValueChange={(value) =>
									setActionItemFormData({
										...actionItemFormData,
										priority: value as ActionItemState["priority"],
									})
								}
							>
								<SelectTrigger className="w-[200px]">
									<SelectValue
										placeholder={t({
											id: "selectPriority",
											message: "Select Priority",
										})}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="low">
										{t({ id: "low", message: "low" })}
									</SelectItem>
									<SelectItem value="normal">
										{t({ id: "normal", message: "Normal" })}
									</SelectItem>
									<SelectItem value="urgent">
										{t({ id: "urgent", message: "Urgent" })}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>{t({ id: "tags", message: "Tags" })}</Label>
							<div className="flex flex-wrap gap-2 mb-2">
								{actionItemFormData.tags.map((tag, index) => (
									<Badge
										key={index}
										variant="secondary"
										className="text-xs flex items-center space-x-1 rtl:space-x-reverse"
									>
										<span>{tag}</span>
										<button
											onClick={() => handleRemoveActionItemTag(tag)}
											className="ml-1 rtl:ml-0 rtl:mr-1 hover:text-danger-ink"
										>
											<X className="w-3 h-3" />
										</button>
									</Badge>
								))}
							</div>
							<div className="flex space-x-2 rtl:space-x-reverse">
								<Input
									value={actionItemNewTag}
									onChange={(e) => setActionItemNewTag(e.target.value)}
									placeholder={t({ id: "addTag", message: "Add Tag" })}
									onKeyPress={(e) =>
										e.key === "Enter" && handleAddActionItemTag()
									}
									{...contentFieldProps(actionItemNewTag)}
								/>
								<Button
									type="button"
									onClick={handleAddActionItemTag}
									size="sm"
								>
									{t({ id: "addTag", message: "Add Tag" })}
								</Button>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsEditActionItemOpen(false)}
						>
							{t({ id: "cancel", message: "Cancel" })}
						</Button>
						<Button onClick={handleSaveActionItem}>
							{t({ id: "saveChanges", message: "Save Changes" })}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Create Action Item Dialog */}
			<Dialog
				open={isCreateActionItemOpen}
				onOpenChange={setIsCreateActionItemOpen}
			>
				<DialogContent className="sm:max-w-[500px]">
					<DialogHeader>
						<DialogTitle>
							{t({ id: "createActionItem", message: "Create Action Item" })}
						</DialogTitle>
						<DialogDescription>
							{t({
								id: "createActionItemDescription",
								message: "Add a follow-up task for this call",
							})}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="create-action-item-title">
								{t({ id: "title", message: "Title" })}
							</Label>
							<Input
								id="create-action-item-title"
								value={createActionItemFormData.title}
								onChange={(e) =>
									setCreateActionItemFormData({
										...createActionItemFormData,
										title: e.target.value,
									})
								}
								{...contentFieldProps(createActionItemFormData.title)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="create-action-item-description">
								{t({ id: "description", message: "Description" })}
							</Label>
							<Textarea
								id="create-action-item-description"
								value={createActionItemFormData.description}
								onChange={(e) =>
									setCreateActionItemFormData({
										...createActionItemFormData,
										description: e.target.value,
									})
								}
								rows={2}
								{...contentFieldProps(createActionItemFormData.description)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="create-action-item-notes">
								{t({ id: "notes", message: "Notes" })}
							</Label>
							<Textarea
								id="create-action-item-notes"
								value={createActionItemFormData.notes}
								onChange={(e) =>
									setCreateActionItemFormData({
										...createActionItemFormData,
										notes: e.target.value,
									})
								}
								rows={3}
								placeholder={t({ id: "addNote", message: "Add Note" })}
								{...contentFieldProps(createActionItemFormData.notes)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="create-action-item-status">
								{t({ id: "status", message: "Status" })}
							</Label>
							<Select
								value={createActionItemFormData.status}
								onValueChange={(value) =>
									setCreateActionItemFormData({
										...createActionItemFormData,
										status: value as ActionItemState["status"],
									})
								}
							>
								<SelectTrigger className="w-[200px]">
									<SelectValue
										placeholder={t({
											id: "selectStatus",
											message: "Select Status",
										})}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="open">
										{t({ id: "open", message: "Open" })}
									</SelectItem>
									<SelectItem value="in-progress">
										{t({ id: "in-progress", message: "In Progress" })}
									</SelectItem>
									<SelectItem value="closed">
										{t({ id: "closed", message: "Closed" })}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="create-action-item-severity">
								{t({ id: "priority", message: "Priority" })}
							</Label>
							<Select
								value={createActionItemFormData.priority}
								onValueChange={(value) =>
									setCreateActionItemFormData({
										...createActionItemFormData,
										priority: value as ActionItemState["priority"],
									})
								}
							>
								<SelectTrigger className="w-[200px]">
									<SelectValue
										placeholder={t({
											id: "selectPriority",
											message: "Select Priority",
										})}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="low">
										{t({ id: "low", message: "low" })}
									</SelectItem>
									<SelectItem value="normal">
										{t({ id: "normal", message: "Normal" })}
									</SelectItem>
									<SelectItem value="urgent">
										{t({ id: "urgent", message: "Urgent" })}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>{t({ id: "tags", message: "Tags" })}</Label>
							<div className="flex flex-wrap gap-2 mb-2">
								{createActionItemFormData.tags.map((tag, index) => (
									<Badge
										key={index}
										variant="secondary"
										className="text-xs flex items-center space-x-1 rtl:space-x-reverse"
									>
										<span>{tag}</span>
										<button
											onClick={() => handleRemoveCreateActionItemTag(tag)}
											className="ml-1 rtl:ml-0 rtl:mr-1 hover:text-danger-ink"
										>
											<X className="w-3 h-3" />
										</button>
									</Badge>
								))}
							</div>
							<div className="flex space-x-2 rtl:space-x-reverse">
								<Input
									value={createActionItemNewTag}
									onChange={(e) => setCreateActionItemNewTag(e.target.value)}
									placeholder={t({ id: "addTag", message: "Add Tag" })}
									onKeyPress={(e) =>
										e.key === "Enter" && handleAddCreateActionItemTag()
									}
									{...contentFieldProps(createActionItemNewTag)}
								/>
								<Button
									type="button"
									onClick={handleAddCreateActionItemTag}
									size="sm"
								>
									{t({ id: "addTag", message: "Add Tag" })}
								</Button>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsCreateActionItemOpen(false)}
						>
							{t({ id: "cancel", message: "Cancel" })}
						</Button>
						<Button onClick={handleCreateActionItem}>
							{t({ id: "create", message: "Create" })}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
